import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useConfig } from 'hooks/use-config';
import { skillsService } from './skills.service';
import {
  CreateSkillPayload,
  GetSkillsPayload,
  ImportSkillPayload,
  OptimizeSkillFieldPayload,
  UpdateLearnedSkillPayload,
  UpdateSkillPayload,
  UpdateSkillResourcePayload,
} from './skills.types';

export const useGetSkills = (
  payload: GetSkillsPayload,
  queryOptions?: { enabled?: boolean; scope?: string }
) => {
  const { configurationSettings } = useConfig();
  const blocksKey = configurationSettings?.blocksKey || '';

  return useQuery({
    queryKey: ['skills', queryOptions?.scope ?? 'default', payload],
    queryFn: () => skillsService.getSkills(payload, blocksKey),
    enabled: !!blocksKey && (queryOptions?.enabled ?? true),
  });
};

/** Agents V2 picker: GET /skills?page=1&page_size=20&surface=agents_v2 */
export const useGetAgentSkills = (
  payload: { page?: number; pageSize?: number; search_query?: string } = {},
  queryOptions?: { enabled?: boolean; scope?: string }
) => {
  const { page = 1, pageSize = 20, search_query } = payload;
  const trimmedSearch = search_query?.trim();

  return useGetSkills(
    {
      page,
      pageSize,
      surface: 'agents_v2',
      ...(trimmedSearch ? { search_query: trimmedSearch } : {}),
    },
    queryOptions
  );
};

export const AGENT_SKILLS_PAGE_SIZE = 20;

/** Infinite scroll for Agents V2 skill picker. */
export const useGetAgentSkillsInfinite = (
  payload: { pageSize?: number; search_query?: string } = {},
  queryOptions?: { enabled?: boolean; scope?: string }
) => {
  const { configurationSettings } = useConfig();
  const blocksKey = configurationSettings?.blocksKey || '';
  const pageSize = payload.pageSize ?? AGENT_SKILLS_PAGE_SIZE;
  const trimmedSearch = payload.search_query?.trim();

  return useInfiniteQuery({
    queryKey: [
      'skills',
      'infinite',
      queryOptions?.scope ?? 'agent-skills',
      { pageSize, surface: 'agents_v2', search_query: trimmedSearch || undefined },
    ],
    queryFn: ({ pageParam = 1 }) =>
      skillsService.getSkills(
        {
          page: pageParam,
          pageSize,
          surface: 'agents_v2',
          ...(trimmedSearch ? { search_query: trimmedSearch } : {}),
        },
        blocksKey
      ),
    getNextPageParam: (lastPage, allPages) => {
      const totalFetched = allPages.reduce((sum, page) => sum + page.data.length, 0);
      if (totalFetched < lastPage.totalCount) {
        return lastPage.page + 1;
      }
      return undefined;
    },
    initialPageParam: 1,
    enabled: !!blocksKey && (queryOptions?.enabled ?? true),
  });
};

export const useGetSkillById = (skillId: string, enabled = true) => {
  const { configurationSettings } = useConfig();
  const blocksKey = configurationSettings?.blocksKey || '';

  return useQuery({
    queryKey: ['skills', skillId],
    queryFn: () => skillsService.getSkillById(skillId, blocksKey),
    enabled: !!blocksKey && !!skillId && enabled,
  });
};

export const useCreateSkill = () => {
  const { configurationSettings } = useConfig();
  const blocksKey = configurationSettings?.blocksKey || '';
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['skills', 'create'],
    mutationFn: (payload: CreateSkillPayload) => skillsService.createSkill(payload, blocksKey),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['skills'] });
    },
  });
};

export const useInstallSkill = () => {
  const { configurationSettings } = useConfig();
  const blocksKey = configurationSettings?.blocksKey || '';
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['skills', 'install'],
    mutationFn: (skillId: string) => skillsService.installSkill(skillId, blocksKey),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['skills'] });
    },
  });
};

export const useUninstallSkill = () => {
  const { configurationSettings } = useConfig();
  const blocksKey = configurationSettings?.blocksKey || '';
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['skills', 'uninstall'],
    mutationFn: (skillId: string) => skillsService.uninstallSkill(skillId, blocksKey),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['skills'] });
    },
  });
};

export const useUpdateSkill = () => {
  const { configurationSettings } = useConfig();
  const blocksKey = configurationSettings?.blocksKey || '';
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['skills', 'update'],
    mutationFn: ({ skillId, payload }: { skillId: string; payload: UpdateSkillPayload }) =>
      skillsService.updateSkill(skillId, payload, blocksKey),
    onSuccess: (skill, variables) => {
      queryClient.setQueryData(['skills', variables.skillId], skill);
      queryClient.invalidateQueries({
        queryKey: ['skills'],
        predicate: (query) => query.queryKey[1] !== variables.skillId,
      });
    },
  });
};

export const useDeleteSkill = () => {
  const { configurationSettings } = useConfig();
  const blocksKey = configurationSettings?.blocksKey || '';
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['skills', 'delete'],
    mutationFn: (skillId: string) => skillsService.deleteSkill(skillId, blocksKey),
    onSuccess: (_, skillId) => {
      queryClient.invalidateQueries({ queryKey: ['skills'] });
      queryClient.removeQueries({ queryKey: ['skills', skillId] });
    },
  });
};

export const useImportSkill = () => {
  const { configurationSettings } = useConfig();
  const blocksKey = configurationSettings?.blocksKey || '';
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['skills', 'import'],
    mutationFn: (payload: ImportSkillPayload) => skillsService.importSkill(payload, blocksKey),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['skills'] });
    },
  });
};

export const useDuplicateSkill = () => {
  const { configurationSettings } = useConfig();
  const blocksKey = configurationSettings?.blocksKey || '';
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['skills', 'duplicate'],
    mutationFn: (skillId: string) => skillsService.duplicateSkill(skillId, blocksKey),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['skills'] });
    },
  });
};

export const useDownloadSkill = () => {
  const { configurationSettings } = useConfig();
  const blocksKey = configurationSettings?.blocksKey || '';

  return useMutation({
    mutationKey: ['skills', 'download'],
    mutationFn: (skillId: string) => skillsService.downloadSkill(skillId, blocksKey),
  });
};

export const useUploadSkillResource = () => {
  const { configurationSettings } = useConfig();
  const blocksKey = configurationSettings?.blocksKey || '';
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['skills', 'resource', 'upload'],
    mutationFn: ({ skillId, file }: { skillId: string; file: File }) =>
      skillsService.uploadSkillResource(skillId, file, blocksKey),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['skills'] });
      queryClient.invalidateQueries({ queryKey: ['skills', variables.skillId] });
    },
  });
};

export const useDeleteSkillResource = () => {
  const { configurationSettings } = useConfig();
  const blocksKey = configurationSettings?.blocksKey || '';
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['skills', 'resource', 'delete'],
    mutationFn: ({ skillId, fileId }: { skillId: string; fileId: string }) =>
      skillsService.deleteSkillResource(skillId, fileId, blocksKey),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['skills'] });
      queryClient.invalidateQueries({ queryKey: ['skills', variables.skillId] });
    },
  });
};

export const useUpdateSkillResource = () => {
  const { configurationSettings } = useConfig();
  const blocksKey = configurationSettings?.blocksKey || '';
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['skills', 'resource', 'update'],
    mutationFn: ({ skillId, fileId, content }: UpdateSkillResourcePayload) =>
      skillsService.updateSkillResource(skillId, fileId, content, blocksKey),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['skills'] });
      queryClient.invalidateQueries({ queryKey: ['skills', variables.skillId] });
    },
  });
};

export const useDraftSkill = () => {
  const { configurationSettings } = useConfig();
  const blocksKey = configurationSettings?.blocksKey || '';

  return useMutation({
    mutationKey: ['skills', 'draft'],
    mutationFn: (userPrompt: string) => skillsService.draftSkill(userPrompt, blocksKey),
  });
};

const LEARNED_SKILLS_PAGE_SIZE = 20;

function learnedSkillsKey(agentId: string) {
  return ['learned-skills', agentId] as const;
}

export function useGetLearnedSkills(agentId: string) {
  const { configurationSettings } = useConfig();
  const blocksKey = configurationSettings?.blocksKey || '';

  return useInfiniteQuery({
    queryKey: learnedSkillsKey(agentId),
    queryFn: ({ pageParam }) =>
      skillsService.getLearnedSkills(
        { agentId, page: pageParam, pageSize: LEARNED_SKILLS_PAGE_SIZE },
        blocksKey
      ),
    initialPageParam: 1,
    getNextPageParam: (lastPage) => {
      if (lastPage.page * lastPage.pageSize < lastPage.totalCount) {
        return lastPage.page + 1;
      }
      return undefined;
    },
    enabled: !!blocksKey && !!agentId,
  });
}

export function useGetLearnedSkill(agentId: string, skillId: string | null, enabled = true) {
  const { configurationSettings } = useConfig();
  const blocksKey = configurationSettings?.blocksKey || '';

  return useQuery({
    queryKey: ['learned-skills', agentId, skillId],
    queryFn: () => skillsService.getLearnedSkill(agentId, skillId as string, blocksKey),
    enabled: !!blocksKey && !!agentId && !!skillId && enabled,
  });
}

export function useUpdateLearnedSkill(agentId: string) {
  const { configurationSettings } = useConfig();
  const blocksKey = configurationSettings?.blocksKey || '';
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ skillId, payload }: { skillId: string; payload: UpdateLearnedSkillPayload }) =>
      skillsService.updateLearnedSkill(agentId, skillId, payload, blocksKey),
    onSuccess: (skill) => {
      queryClient.setQueryData(['learned-skills', agentId, skill.id], skill);
      void queryClient.invalidateQueries({ queryKey: learnedSkillsKey(agentId), exact: true });
    },
  });
}

export function useDeleteLearnedSkill(agentId: string) {
  const { configurationSettings } = useConfig();
  const blocksKey = configurationSettings?.blocksKey || '';
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (skillId: string) => skillsService.deleteLearnedSkill(agentId, skillId, blocksKey),
    onSuccess: (_result, skillId) => {
      queryClient.removeQueries({ queryKey: ['learned-skills', agentId, skillId] });
      void queryClient.invalidateQueries({ queryKey: learnedSkillsKey(agentId), exact: true });
    },
  });
}

export const useOptimizeSkillField = () => {
  const { configurationSettings } = useConfig();
  const blocksKey = configurationSettings?.blocksKey || '';

  return useMutation({
    mutationKey: ['skills', 'optimize'],
    mutationFn: (payload: OptimizeSkillFieldPayload) =>
      skillsService.optimizeSkillField(payload, blocksKey),
  });
};
